import { useEffect, useState } from 'react';
import { Descriptions, Divider, Flex, Select, Space } from 'antd';
import { TitleLayout } from 'components/layouts';
import { DataGrid, Editor, ConfirmButton, DeleteButton, ListButton, NewTag } from 'components/elements';
import { BaseContainer } from 'components/containers';
import { IconDown, IconPaperClip, IconUp } from 'components/assets/icons';
import { Upload, Attachment } from 'pages/common';
import dayjs from 'dayjs';
import { deleteCustomerCenterInquiryAPI, getCustomerCenterInquiryDetailAPI, putCustomerCenterInquiryAPI } from 'api/customer-center';
import { customerCenterInquiryTypeOptions, inquiryProcessStatusOptions } from 'data/options';
import { getFormatDate, objToLowerCase } from 'utils/utils';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { URL_ADMIN } from 'routes/AdminRoute';
import { URL_CUSTOMER_CENTER_INQUIRY, URL_CUSTOMER_CENTER_INQUIRY_DETAIL } from 'routes/CommonRoute';
import { ReplyAttachmentWrapper } from 'components/styled/wrapper';

const CustomerCenterInquiryDetail = () => {
  const { no } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [value, setValue] = useState({
    addFileName: [],
    addFilePath: []
  });
  const [open, setOpen] = useState({ main: true, reply: true });
  const [inquiryType, setInquiryType] = useState(null);

  const fnGetDetail = async () => {
    const params = {
      inquiry_type: inquiryType
    }
    await getCustomerCenterInquiryDetailAPI(no, params)
      .then((res) => {
        const { data_info } = res?.data;
        setValue({
          ...value,
          ...objToLowerCase(data_info)
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fnUpdate = async () => {
    if (!value?.reply_content) {
      alert('내용을 입력해주세요.');
      return false;
    }
    let data = {
      ...value,
      file_list: {
        ...value?.file_list,
        REPLY_ATTACHED_FILE: value?.addReplyFileName ? (value?.file_list?.REPLY_ATTACHED_FILE || []).concat(value?.addReplyFileName) : value?.file_list?.REPLY_ATTACHED_FILE,
        REPLY_ATTACHED_FILE_PATH: value?.addReplyFilePath ? (value?.file_list?.REPLY_ATTACHED_FILE_PATH || []).concat(value?.addReplyFilePath) : value?.file_list?.REPLY_ATTACHED_FILE_PATH
      }
    };
    delete data.addReplyFileName;
    delete data.addReplyFilePath;

    await putCustomerCenterInquiryAPI(data)
      .then((res) => {
        alert('등록되었습니다.');
        navigate(`${URL_ADMIN}${URL_CUSTOMER_CENTER_INQUIRY}`);
      })
      .catch((err) => console.error(err))
      .finally(() => {});
  };

  const fnDelete = async () => {
    const data = [value?.idx];
    await deleteCustomerCenterInquiryAPI(data)
      .then((res) => {
        alert('삭제되었습니다.');
        navigate(`${URL_ADMIN}${URL_CUSTOMER_CENTER_INQUIRY}`);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (state) setInquiryType(state?.inquiryType);
  }, [state])

  useEffect(() => {
    if (inquiryType && no) fnGetDetail();
  }, [inquiryType, no])

  const titleColumns = [
    {
      key: '1',
      title: (
        <>
          {value?.title}
          {dayjs(value?.regist_date).isSame(dayjs(), 'day') && <NewTag />}
        </>
      ),
      dataIndex: 'inquiry_type',
      align: 'center',
      width: '20%',
      colSpan: 5,
      render: (text) => <Space>카테고리 : {customerCenterInquiryTypeOptions?.find((it) => it.value === text)?.label}</Space>
    },
    { key: '2', title: '', dataIndex: 'process_status', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>처리상태 : {inquiryProcessStatusOptions?.find((it) => it.value === text)?.label}</Space> },
    { key: '2', title: '', dataIndex: 'writer', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>작성자 : {text}</Space> },
    { key: '3', title: '', dataIndex: 'regist_date', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>등록일 : {getFormatDate(text)}</Space> },
    { key: '4', title: '', dataIndex: 'hits', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>조회수 : {text}</Space> }
  ];

  const mainFileColumns = [
    {
      key: '1',
      title: '',
      dataIndex: 'attachment',
      render: (text, record) => (
        <>
          {record?.file_list?.ATTACHED_FILE_PATH && (
            <>
              {open?.main ? (
                <IconUp onClick={() => setOpen({ ...open, main: false })} />
              ) : (
                <IconDown onClick={() => setOpen({ ...open, main: true })} />
              )} 
              첨부
              {open?.main && (
                <div className="attachment-container">
                  <Space direction={'vertical'} size={1}>
                    {record?.file_list?.ATTACHED_FILE_PATH?.map((item, index) => (
                      <Attachment
                        key={index}
                        label={`${record?.file_list?.ATTACHED_FILE[index]}`}
                        icon={<IconPaperClip />}
                        url={item}
                      />
                    ))}
                  </Space>
                </div>
              )}
            </>
          )}
        </>
      )
    }
  ];

  const navigateItems = [
    { key: '1', 
      label: '이전글', 
      children: (
        <span 
        className="text-click" 
        onClick={() =>
          navigate(`${URL_ADMIN}${URL_CUSTOMER_CENTER_INQUIRY_DETAIL.replace(':no', value?.prev?.IDX)}`, {state: { inquiryType: value?.inquiry_type }})}
        >{value?.prev?.TITLE}</span>
      )
    },
    { key: '2', 
      label: '다음글', 
      children: (
        <span 
          className="text-click" 
          onClick={() =>
            navigate(`${URL_ADMIN}${URL_CUSTOMER_CENTER_INQUIRY_DETAIL.replace(':no', value?.next?.IDX)}`, {state: { inquiryType: value?.inquiry_type }})}
        >{value?.next?.TITLE}</span>
      )
    }
  ]

  const replyFileColumns = [
    {
      key: '1',
      title: '',
      dataIndex: 'reply_attached_file',
      render: (text, record) => (
        <>
          {record?.file_list?.REPLY_ATTACHED_FILE_PATH && (
            <>
              {open?.main ? (
                <IconUp onClick={() => setOpen({ ...open, reply: false })} />
              ) : (
                <IconDown onClick={() => setOpen({ ...open, reply: true })} />
              )} 
              첨부
              {open?.main && (
                <div className="attachment-container">
                  <Space direction={'vertical'} size={1}>
                    {record?.file_list?.REPLY_ATTACHED_FILE_PATH?.map((item, index) => (
                      <Attachment
                        key={index}
                        label={`${record?.file_list?.REPLY_ATTACHED_FILE[index]}`}
                        icon={<IconPaperClip />}
                        url={item}
                      />
                    ))}
                  </Space>
                </div>
              )}
            </>
          )}
        </>
      )
    }
  ];

  return value?.idx ? (
    <BaseContainer>
      <DataGrid columns={titleColumns} data={[value]} />
      {value?.attached_file?.length && <DataGrid showHeader={false} bordered={false} columns={mainFileColumns} data={[value]} />}
      <div style={{ padding: '2rem 1rem', minHeight: '200px' }} dangerouslySetInnerHTML={{ __html: value?.content }} />
      <div style={{ padding: '1rem', backgroundColor: 'whitesmoke' }}>
        <TitleLayout level={5}>답변</TitleLayout>
        <Divider className="divider" />

        <Space className={'reply-attachment-container'}>
          처리상태 :
          <Select value={value?.process_status} style={{ width: '15rem' }} options={inquiryProcessStatusOptions?.slice(1)} onSelect={(v) => setValue({ ...value, process_status: v })} />
        </Space>
        <Divider className="divider-small" />

        <Editor data={value?.reply_content} onChange={(v) => setValue({ ...value, reply_content: v })} />
        <Divider className="divider-small" />
        {value?.file_list?.REPLY_ATTACHED_FILE_PATH && (
          <ReplyAttachmentWrapper>
            <DataGrid showHeader={false} bordered={false} columns={replyFileColumns} data={[value]} />
          </ReplyAttachmentWrapper>
        )}
        <Upload
          className={'reply-attachment-container'}
          data={[]}
          onChange={(v) =>
            setValue({
              ...value,
              addReplyFilePath: v?.map((item) => item?.file_path),
              addReplyFileName: v?.map((item) => item?.origin_file_name)
            })
          }
        />
        <Divider className="divider-small" />
      </div>

      <Divider className="divider" />
      <Descriptions items={navigateItems} column={1} bordered labelStyle={{ width: '10%', textAlign: 'center' }} contentStyle={{ textAlign: 'left' }} />
      <Divider className="divider" />

      <Flex justify={'end'} gap={3}>
        <ConfirmButton label={'작성완료'} onClick={fnUpdate} />
        <DeleteButton onClick={fnDelete} />
        <ListButton onClick={() => navigate(`${URL_ADMIN}${URL_CUSTOMER_CENTER_INQUIRY}`)} />
      </Flex>
    </BaseContainer>
  ) : null;
};

export default CustomerCenterInquiryDetail;
